import { useCallback, useEffect, useState } from "react";
import {
  CalendarProvider,
  ConnectionStatus,
} from "@photography-booking/shared";
import type { AdminCalendarConnection } from "../api/admin-client.js";
import {
  blockDates,
  connectCalendar,
  disconnectCalendar,
  listBlockedDates,
  unblockDates,
  getGoogleAuthUrl,
  listCalendarEvents,
  listCalendars,
  type CalendarEventView,
} from "../api/admin-client.js";
import { AdminPanel, useAdminStatus } from "./AdminPanel.js";
import { BlockedDatesCalendar } from "./BlockedDatesCalendar.js";
import { isConnectionStale } from "./calendar-health.js";

/** Formats an event's date/time range for the read-only calendar list. */
function formatEventWhen(event: CalendarEventView): string {
  const start = new Date(event.start);
  // Render in the event's own zone when known (the Google calendar default can
  // be "UTC", so relying on the viewer's browser zone would be off by the
  // offset); fall back to the browser zone otherwise.
  const zone = event.timeZone;
  if (event.allDay) {
    return start.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      ...(zone ? { timeZone: zone } : {}),
    });
  }
  const date = start.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(zone ? { timeZone: zone } : {}),
  });
  const time = start.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    ...(zone ? { timeZone: zone } : {}),
  });
  return `${date} · ${time}`;
}

interface CalendarAdminProps {
  token: string;
}

/** A run of consecutive blocked days, shown as one row. */
interface BlockedRun {
  from: string;
  to: string;
  days: number;
}

/** The day after `date` (ISO YYYY-MM-DD). */
function nextDay(date: string): string {
  const d = new Date(`${date}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Collapses sorted blocked dates into consecutive runs, so a week of travel
 * reads as a single "Sep 1 – Sep 7 (7 days)" row instead of seven rows.
 */
function groupBlockedDates(dates: readonly string[]): BlockedRun[] {
  const sorted = [...dates].sort();
  const runs: BlockedRun[] = [];
  for (const date of sorted) {
    const current = runs[runs.length - 1];
    if (current && nextDay(current.to) === date) {
      current.to = date;
      current.days += 1;
    } else {
      runs.push({ from: date, to: date, days: 1 });
    }
  }
  return runs;
}

/** Formats a run as a friendly label, e.g. "Sep 1 – Sep 3, 2026 (3 days)". */
function formatRun(run: BlockedRun): string {
  const fmt = (iso: string): string => {
    const d = new Date(`${iso}T12:00:00.000Z`);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        });
  };
  return run.days === 1
    ? fmt(run.from)
    : `${fmt(run.from)} – ${fmt(run.to)} (${run.days} days)`;
}

/** Human-readable provider labels for the connect control. */
const PROVIDER_LABELS: Record<CalendarProvider, string> = {
  [CalendarProvider.GOOGLE]: "Google Calendar",
  [CalendarProvider.ICLOUD]: "iCloud",
  [CalendarProvider.OUTLOOK]: "Outlook",
};

/** A single credential input the owner fills in for a provider. */
interface CredentialField {
  /** Key sent to the API (must match the backend's required field name). */
  name: string;
  /** Friendly label shown to the owner. */
  label: string;
  /** Whether the value is a secret (rendered as a password input). */
  secret?: boolean;
  /** Short hint on where to obtain the value. */
  hint?: string;
}

/**
 * The credential fields each provider requires, mirroring the backend's
 * per-provider validation. iCloud is the easiest to set up (an Apple ID plus
 * an app-specific password); Google and Outlook need an OAuth app registration
 * to obtain a refresh token.
 */
const PROVIDER_FIELDS: Record<CalendarProvider, readonly CredentialField[]> = {
  [CalendarProvider.GOOGLE]: [
    { name: "clientId", label: "OAuth client ID" },
    { name: "clientSecret", label: "OAuth client secret", secret: true },
    { name: "refreshToken", label: "Refresh token", secret: true },
    {
      name: "calendarId",
      label: "Calendar ID (optional)",
      hint: "Defaults to your primary calendar.",
    },
  ],
  [CalendarProvider.ICLOUD]: [
    { name: "username", label: "Apple ID email" },
    {
      name: "appSpecificPassword",
      label: "App-specific password",
      secret: true,
      hint: "Create one at appleid.apple.com › Sign-In & Security.",
    },
    {
      name: "calendarUrl",
      label: "Calendar URL",
      hint: "Your iCloud CalDAV calendar collection URL.",
    },
  ],
  [CalendarProvider.OUTLOOK]: [
    { name: "tenantId", label: "Azure tenant ID" },
    { name: "clientId", label: "OAuth client ID" },
    { name: "clientSecret", label: "OAuth client secret", secret: true },
    { name: "refreshToken", label: "Refresh token", secret: true },
    { name: "scheduleAddress", label: "Mailbox address" },
  ],
};

/** Fields that may be left blank (everything else is required). */
const OPTIONAL_FIELDS = new Set(["calendarId"]);

/**
 * Calendar connections view (Req 7.1, 7.9). Lists connected calendars
 * (`GET /admin/calendars`), connects a new provider with its credentials
 * (`POST /admin/calendars`), and disconnects one (`DELETE /admin/calendars/{id}`).
 * A disconnected calendar stops contributing busy dates to availability, and
 * its stored credentials are removed.
 */
export function CalendarAdmin({ token }: CalendarAdminProps): JSX.Element {
  const [connections, setConnections] = useState<AdminCalendarConnection[]>([]);
  // Google is the default: it is the one-click flow and the provider nearly
  // every owner uses.
  const [provider, setProvider] = useState<CalendarProvider>(
    CalendarProvider.GOOGLE,
  );
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState<Record<string, CalendarEventView[]>>({});
  const [eventsLoading, setEventsLoading] = useState<string | null>(null);
  // Owner-closed dates: shown as unavailable on the public booking calendar.
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [blockBusy, setBlockBusy] = useState(false);
  const { status, reportError, reportSuccess, clearStatus } = useAdminStatus();

  const loadEvents = useCallback(
    async (connectionId: string) => {
      setEventsLoading(connectionId);
      const result = await listCalendarEvents(token, connectionId);
      setEventsLoading(null);
      if (result.ok) {
        setEvents((current) => ({ ...current, [connectionId]: result.data }));
      } else {
        reportError(result.message);
      }
    },
    [token, reportError],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await listCalendars(token);
    setLoading(false);
    if (result.ok) {
      setConnections(result.data);
    } else {
      reportError(result.message);
    }
  }, [token, reportError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Surface the outcome of the Google OAuth redirect (the callback returns to
  // `?calendar=<outcome>`), then clean the flag from the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("calendar");
    if (!outcome) {
      return;
    }
    if (outcome === "google_connected") {
      reportSuccess("Google Calendar connected. Its busy dates will sync shortly.");
    } else if (outcome === "google_no_refresh") {
      reportError(
        "Google didn't return a refresh token. Remove the app's access in your Google account, then try connecting again.",
      );
    } else if (outcome === "google_error") {
      reportError("We couldn't connect Google Calendar. Please try again.");
    }
    params.delete("calendar");
    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
    );
  }, [reportSuccess, reportError]);

  /** Loads the Owner-closed dates for the blocked-dates section. */
  const refreshBlocked = useCallback(async () => {
    const result = await listBlockedDates(token);
    if (result.ok) {
      setBlockedDates(result.data);
    }
  }, [token]);

  useEffect(() => {
    void refreshBlocked();
  }, [refreshBlocked]);

  /** Closes the selected day, or the whole span when a range was chosen. */
  const handleBlock = async (from: string, to: string): Promise<void> => {
    clearStatus();
    setBlockBusy(true);
    const result = await blockDates(token, from, to);
    setBlockBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setBlockedDates(result.data);
    reportSuccess(
      from === to
        ? "Date closed — visitors can no longer request it."
        : "Dates closed — visitors can no longer request them.",
    );
  };

  /** Re-opens a blocked day or a whole contiguous run of blocked days. */
  const handleUnblock = async (from: string, to: string): Promise<void> => {
    clearStatus();
    setBlockBusy(true);
    const result = await unblockDates(token, from, to);
    setBlockBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setBlockedDates(result.data);
    reportSuccess(
      from === to ? "Date re-opened." : "Dates re-opened for booking.",
    );
  };

  /** Switching provider clears any half-entered credentials. */
  const handleProviderChange = (next: CalendarProvider): void => {
    setProvider(next);
    setValues({});
    clearStatus();
  };

  /** Starts the one-click Google OAuth flow by redirecting to Google's consent. */
  const handleConnectGoogle = async (): Promise<void> => {
    clearStatus();
    setBusy(true);
    const result = await getGoogleAuthUrl(token);
    if (!result.ok) {
      setBusy(false);
      reportError(result.message);
      return;
    }
    // Hand off to Google; the callback returns to the admin with an outcome.
    window.location.href = result.data.url;
  };

  const fields = PROVIDER_FIELDS[provider];
  const isGoogle = provider === CalendarProvider.GOOGLE;

  /** True once every required field for the chosen provider has a value. */
  const readyToConnect = fields.every(
    (field) =>
      OPTIONAL_FIELDS.has(field.name) ||
      (values[field.name] ?? "").trim().length > 0,
  );

  const handleConnect = async (): Promise<void> => {
    clearStatus();
    setBusy(true);
    // Only send non-empty values (so an optional blank field is omitted).
    const credentials: Record<string, string> = {};
    for (const field of fields) {
      const value = (values[field.name] ?? "").trim();
      if (value.length > 0) {
        credentials[field.name] = value;
      }
    }
    const result = await connectCalendar(token, provider, credentials);
    setBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setValues({});
    reportSuccess(
      `${PROVIDER_LABELS[provider]} connected. Its busy dates will sync shortly.`,
    );
    await refresh();
  };

  const handleDisconnect = async (connectionId: string): Promise<void> => {
    clearStatus();
    setBusy(true);
    const result = await disconnectCalendar(token, connectionId);
    setBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setConnections((current) =>
      current.filter((c) => c.connectionId !== connectionId),
    );
    reportSuccess("Calendar disconnected.");
  };

  return (
    <AdminPanel
      title="Calendar connections"
      description="Connect a calendar so booking requests that clash with your plans are flagged. Approved bookings are added to a connected Google Calendar. Credentials are stored securely and never shown again."
      status={status}
    >
      <div className="admin-field">
        <label className="admin-field__label" htmlFor="calendar-provider">
          Connect a calendar
        </label>
        <select
          id="calendar-provider"
          className="admin-field__input"
          value={provider}
          onChange={(event) =>
            handleProviderChange(event.target.value as CalendarProvider)
          }
        >
          {Object.values(CalendarProvider).map((value) => (
            <option key={value} value={value}>
              {PROVIDER_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      {isGoogle ? (
        <>
          <p className="admin-field__hint">
            Click below and sign in with the Google account whose calendar you
            want to use. Google will ask you to let this site see your calendar
            and add confirmed bookings to it. Tick every box. If Google says it
            hasn't verified the app, click Advanced, then Go to … (unsafe).
            There are no codes to copy.
          </p>
          <button
            type="button"
            className="admin-button admin-button--primary"
            disabled={busy}
            onClick={() => void handleConnectGoogle()}
          >
            {busy ? "Redirecting\u2026" : "Connect Google Calendar"}
          </button>
        </>
      ) : (
        <>
          {fields.map((field) => (
            <div className="admin-field" key={field.name}>
              <label
                className="admin-field__label"
                htmlFor={`cal-cred-${field.name}`}
              >
                {field.label}
              </label>
              <input
                id={`cal-cred-${field.name}`}
                className="admin-field__input"
                type={field.secret ? "password" : "text"}
                autoComplete="off"
                value={values[field.name] ?? ""}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    [field.name]: event.target.value,
                  }))
                }
              />
              {field.hint ? (
                <span className="admin-field__hint">{field.hint}</span>
              ) : null}
            </div>
          ))}

          <button
            type="button"
            className="admin-button admin-button--primary"
            disabled={busy || !readyToConnect}
            onClick={() => void handleConnect()}
          >
            {busy ? "Connecting\u2026" : "Connect calendar"}
          </button>
        </>
      )}

      {loading ? (
        <p className="admin-empty">Loading connections…</p>
      ) : connections.length === 0 ? (
        <p className="admin-empty">No calendars connected.</p>
      ) : (
        <ul className="admin-list">
          {connections.map((connection) => {
            const isGoogleConn =
              connection.provider === CalendarProvider.GOOGLE;
            const connEvents = events[connection.connectionId];
            const stale = isConnectionStale(connection, Date.now());
            return (
              <li
                key={connection.connectionId}
                className="admin-list__row admin-list__row--stack"
              >
                <div className="admin-list__row">
                  <div className="admin-list__text">
                    <p className="admin-list__label">
                      {PROVIDER_LABELS[connection.provider]}
                      {connection.status === ConnectionStatus.ERROR ? (
                        <span className="admin-badge admin-badge--conflict">
                          ⚠ Reconnect needed
                        </span>
                      ) : stale ? (
                        <span className="admin-badge admin-badge--conflict">
                          ⚠ Not syncing
                        </span>
                      ) : (
                        <> · {connection.status}</>
                      )}
                    </p>
                    {/* Which Google account is linked. Without this, a calendar
                        connected from the wrong signed-in account is invisible. */}
                    {connection.accountEmail ? (
                      <p className="admin-list__sub">
                        Connected account: <strong>{connection.accountEmail}</strong>
                      </p>
                    ) : isGoogleConn ? (
                      <p className="admin-list__sub admin-list__sub--muted">
                        Connected account unavailable — the authorization may need
                        renewing.
                      </p>
                    ) : null}
                    {connection.status === ConnectionStatus.ERROR ? (
                      /* A revoked/expired authorization is the common cause;
                         until it is reconnected, approved bookings will NOT be
                         added to the calendar. Say that plainly. */
                      <p className="admin-list__sub">
                        This calendar isn’t working right now, so approved
                        bookings are <strong>not</strong> being added to it.
                        Disconnect below, then connect again to fix it.
                      </p>
                    ) : stale ? (
                      <p className="admin-list__sub">
                        This calendar hasn't synced since{" "}
                        {new Date(
                          connection.lastSuccessfulSyncAt as string,
                        ).toLocaleString()}
                        . Approved bookings may not be added to it. Disconnect
                        below, then connect again to fix it.
                      </p>
                    ) : (
                      <p className="admin-list__sub">
                        {connection.lastSuccessfulSyncAt
                          ? `Last synced ${connection.lastSuccessfulSyncAt}`
                          : "Not yet synced"}
                      </p>
                    )}
                  </div>
                  <div className="admin-list__actions">
                    {isGoogleConn ? (
                      <button
                        type="button"
                        className="admin-button"
                        disabled={eventsLoading !== null}
                        onClick={() => void loadEvents(connection.connectionId)}
                      >
                        {eventsLoading === connection.connectionId
                          ? "Loading\u2026"
                          : connEvents
                            ? "Refresh events"
                            : "View calendar"}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="admin-button admin-button--danger"
                      disabled={busy}
                      onClick={() =>
                        void handleDisconnect(connection.connectionId)
                      }
                    >
                      Disconnect
                    </button>
                  </div>
                </div>

                {connEvents ? (
                  <div className="admin-cal-events">
                    <p className="admin-field__label">
                      Your Google Calendar — events over the next 3 months.
                      Private to you; never shown on the public site.
                    </p>
                    {connEvents.length === 0 ? (
                      <p className="admin-empty">
                        No upcoming events on this calendar.
                      </p>
                    ) : (
                      <ul className="admin-cal-events__list">
                        {connEvents.map((ev) => (
                          <li key={ev.id} className="admin-cal-events__item">
                            <span className="admin-cal-events__when">
                              {formatEventWhen(ev)}
                            </span>
                            <span className="admin-cal-events__title">
                              {ev.title}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {/* Owner-closed dates. Unlike the connected calendar (which is private
          and only powers conflict warnings), these deliberately DO show on the
          public booking calendar as unavailable — no reason is ever revealed. */}
      <div className="admin-blocked">
        <h3 className="admin-blocked__title">Blocked dates</h3>
        <p className="admin-list__sub">
          Close specific days so visitors can’t request them — useful for travel
          or days you’re not shooting. They appear unavailable on the booking
          calendar, and no reason is shown. Your connected calendar is never
          revealed publicly.
        </p>
        <BlockedDatesCalendar
          blocked={blockedDates}
          busy={blockBusy}
          onSelectRange={(from, to) => void handleBlock(from, to)}
        />

        {blockedDates.length === 0 ? (
          <p className="admin-empty">
            No blocked dates — every upcoming day is open for booking.
          </p>
        ) : (
          <ul className="admin-list">
            {groupBlockedDates(blockedDates).map((run) => (
              <li key={`${run.from}-${run.to}`} className="admin-list__row">
                <div className="admin-list__text">
                  <p className="admin-list__label">{formatRun(run)}</p>
                  <p className="admin-list__sub">
                    {run.days === 1
                      ? "Closed for booking"
                      : "Closed for booking (whole span)"}
                  </p>
                </div>
                <div className="admin-list__actions">
                  <button
                    type="button"
                    className="admin-button"
                    disabled={blockBusy}
                    onClick={() => void handleUnblock(run.from, run.to)}
                  >
                    {run.days === 1 ? "Re-open" : "Re-open all"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminPanel>
  );
}
